(function() {
'use strict';

/**
 * Shared helper for IsoDash route modules.
 * Bridges project state -> IsoDash.Calculations -> compact derived values.
 */

var cacheKey = '';
var cacheValue = null;

function createRuntimeScope() {
  var listeners = [];
  var timeouts = [];
  var intervals = [];
  var cleanups = [];

  function removeEntry(list, value) {
    var idx = list.indexOf(value);
    if (idx !== -1) list.splice(idx, 1);
  }

  function on(el, event, handler, options) {
    if (!el || typeof el.addEventListener !== 'function' || typeof handler !== 'function') return;
    el.addEventListener(event, handler, options);
    var capture = typeof options === 'boolean' ? options : !!(options && options.capture);
    listeners.push([el, event, handler, capture]);
  }

  function later(fn, ms) {
    var id = setTimeout(() => {
      removeEntry(timeouts, id);
      if (typeof fn === 'function') fn();
    }, ms);
    timeouts.push(id);
    return id;
  }

  function every(fn, ms) {
    var id = setInterval(fn, ms);
    intervals.push(id);
    return id;
  }

  function clearTimeoutTracked(id) {
    if (id === null || id === undefined) return;
    clearTimeout(id);
    removeEntry(timeouts, id);
  }

  function clearIntervalTracked(id) {
    if (id === null || id === undefined) return;
    clearInterval(id);
    removeEntry(intervals, id);
  }

  function trackCleanup(fn) {
    if (typeof fn === 'function') cleanups.push(fn);
  }

  function clear() {
    listeners.forEach(([el, event, handler, capture]) => {
      try { el.removeEventListener(event, handler, capture); } catch (_) {}
    });
    listeners = [];

    timeouts.forEach((id) => clearTimeout(id));
    timeouts = [];

    intervals.forEach((id) => clearInterval(id));
    intervals = [];

    cleanups.forEach((fn) => {
      try { fn(); } catch (_) {}
    });
    cleanups = [];
  }

  return {
    on,
    later,
    every,
    clearTimeout: clearTimeoutTracked,
    clearInterval: clearIntervalTracked,
    trackCleanup,
    clear,
  };
}

function getProject(store) {
  if (!store) return {};
  if (store.project && typeof store.project === 'object') return store.project;
  if (typeof store.get === 'function') {
    var p = store.get();
    return p && typeof p === 'object' ? p : {};
  }
  return {};
}

/**
 * Escape user/storage values before inserting into innerHTML.
 * Prevents XSS from injected content in localStorage/sessionStorage.
 */
var _escMap = {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'};
function escapeHTML(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[&<>"']/g, function(c) { return _escMap[c]; });
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return NaN;
  var n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function numberOr(value, fallback) {
  var n = toNumber(value);
  return Number.isFinite(n) ? n : fallback;
}

function numberFromCandidates(candidates, fallback) {
  for (var i = 0; i < candidates.length; i++) {
    var n = toNumber(candidates[i]);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function mapDiagnosisToMoisture(diagnosis) {
  var d = diagnosis || {};
  var raw = (typeof d === 'string'
    ? d
    : (d.flags && d.flags.moistureType) || d.moistureType || d.type || ''
  );
  var normalized = String(raw).trim().toLowerCase();
  if (
    normalized === 'side' ||
    normalized === 'drueckend' ||
    normalized === 'seitlich'
  ) return 'side';
  return 'bottom';
}

function normalizeMasonryType(type) {
  var raw = String(type || '').trim().toLowerCase();
  if (!raw) return 'ziegel';

  var map = {
    ziegel: 'ziegel',
    ziegelstein: 'ziegel',
    kalksandstein: 'kalksandstein',
    naturstein: 'naturstein',
    beton: 'beton',
    stahlbeton: 'stahlbeton',
    porenbeton: 'porenbeton',
    ytong: 'ytong',
    hohlblock: 'hohlblock',
    hohlblockziegel: 'hohlblock',
    hochlochziegel: 'hochlochziegel',
  };
  if (map[raw]) return map[raw];

  if (raw.includes('stahlbeton')) return 'stahlbeton';
  if (raw.includes('kalksand')) return 'kalksandstein';
  if (raw.includes('natur')) return 'naturstein';
  if (raw.includes('hohl') || raw.includes('loch')) return 'hohlblock';
  if (raw.includes('beton')) return 'beton';
  if (raw.includes('ziegel')) return 'ziegel';
  return raw;
}

function masonryLabel(type) {
  var map = {
    ziegel: 'Ziegel',
    kalksandstein: 'Kalksandstein',
    naturstein: 'Naturstein',
    beton: 'Beton',
    porenbeton: 'Porenbeton',
    ytong: 'Porenbeton',
    hohlblock: 'Hohlblock',
    hochlochziegel: 'Hochlochziegel',
  };
  var normalized = normalizeMasonryType(type);
  return map[normalized] || normalized || 'Ziegel';
}

function formatEuro(value) {
  var n = Number(value) || 0;
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
}

function toCalcState(project) {
  var p = project || {};
  var wall = p.wall || {};
  var diagnosis = p.diagnosis || {};
  var economics = p.economics || {};

  var lengthCm = numberFromCandidates(
    [wall.lengthCm, wall.wallLengthCm],
    800
  );
  var surfaceLengthCm = numberFromCandidates(
    [wall.surfaceLengthCm, wall.lengthCm, wall.wallLengthCm],
    800
  );
  var thicknessCmRaw = numberFromCandidates(
    [wall.thicknessCm, wall.wallThicknessCm],
    36
  );
  var cornersRaw = numberOr(wall.corners, 0);
  var livingArea = Math.max(8, numberOr(economics.livingArea, 30));
  var sideHeightCmRaw = numberFromCandidates(
    [wall.surfaceHeightCm, wall.wallHeightCm],
    0
  );

  var lengthM = Math.max(0.5, lengthCm / 100);
  var surfaceLengthM = Math.max(0.5, surfaceLengthCm / 100);
  var thicknessCm = Math.max(8, thicknessCmRaw);
  var corners = Math.max(0, cornersRaw);
  var sideHeightFromWallM = sideHeightCmRaw > 0 ? (sideHeightCmRaw / 100) : 0;
  var sideHeightFromAreaM = Math.max(2, Math.round((Math.sqrt(livingArea) / 2) * 10) / 10);
  var sideHeight = sideHeightFromWallM > 0 ? sideHeightFromWallM : sideHeightFromAreaM;

  return {
    moistureType: mapDiagnosisToMoisture(diagnosis),
    masonryType: normalizeMasonryType(wall.masonry || 'ziegel'),
    wallLength: lengthM,
    surfaceLength: surfaceLengthM,
    surfaceHeight: sideHeight,
    cornerCount: corners,
    wallThickness: thicknessCm,
    variationFaktor: 1,
  };
}

function buildFallback(project) {
  var p = project || {};
  var wall = p.wall || {};
  var economics = p.economics || {};

  var wallLength = Math.max(0.5, numberFromCandidates([wall.lengthCm, wall.wallLengthCm], 800) / 100);
  var wallThickness = Math.max(8, numberFromCandidates([wall.thicknessCm, wall.wallThicknessCm], 36));
  var corners = Math.max(0, numberOr(wall.corners, 0));
  var holes = Math.max(8, Math.round(wallLength * 4 + corners));
  var spacing = 25;
  var depth = Math.ceil(wallThickness * 2 / 3 / 0.70710678); // v641: Loch reicht ⅔ in die Wand, Länge am Bohrer
  var bottles = Math.max(5, Math.ceil((wallLength * 2.5) + corners));
  var total = bottles * 19.9;
  var firma = Math.max(total + 1000, Math.round(wallLength * 600));
  var savings = Math.max(0, firma - total);
  var savingsPercent = firma > 0 ? Math.round((savings / firma) * 100) : 0;
  var difficulty = holes <= 20 ? 1 : holes <= 35 ? 2 : holes <= 50 ? 3 : holes <= 70 ? 4 : 5;
  var workTimeMin = Math.max(120, Math.round(holes * 5));
  var propertyValue = Number(economics.propertyValue) || 0;

  return {
    source: 'fallback',
    wallLength,
    wallThickness,
    corners,
    masonry: normalizeMasonryType(wall.masonry || 'ziegel'),
    holes,
    spacing,
    depth,
    angle: 45,
    mlPerHole: 500,
    bottles,
    total,
    firma,
    savings,
    savingsPercent,
    difficulty,
    workTimeMin,
    workTimeRangeText: `ca. ${Math.max(90, workTimeMin - 30)}–${workTimeMin + 45} min`,
    drillMachineRecommendation: 'Bohrhammer SDS+ ab 800 W',
    propertyValue,
    riskLossMin: propertyValue * 0.05,
    riskLossMax: propertyValue * 0.15,
  };
}

function toDerived(project, analysis) {
  var p = project || {};
  var wall = p.wall || {};
  var economics = p.economics || {};
  var propertyValue = numberOr(economics.propertyValue, 0);
  var holes = Math.max(0, numberOr(analysis.holeCount, 0));
  var wallLengthFallback = Math.max(0.5, numberFromCandidates([wall.lengthCm, wall.wallLengthCm], 800) / 100);
  var wallThicknessFallback = Math.max(8, numberFromCandidates([wall.thicknessCm, wall.wallThicknessCm], 36));
  var cornersFallback = Math.max(0, numberOr(wall.corners, 0));
  var cornersValue = Math.max(0, numberOr(analysis.cornerCount, cornersFallback));

  var difficulty = holes <= 20 ? 1 : holes <= 35 ? 2 : holes <= 50 ? 3 : holes <= 70 ? 4 : 5;
  var workTimeMin = Math.max(0, numberOr(analysis.workTimeTotalMin, 0));

  return {
    source: 'calculation',
    wallLength: Math.max(0.5, numberOr(analysis.wallLengthM, wallLengthFallback)),
    wallThickness: Math.max(8, numberOr(analysis.wallThicknessCm, wallThicknessFallback)),
    corners: cornersValue,
    masonry: normalizeMasonryType(analysis.masonryType || wall.masonry || 'ziegel'),
    holes,
    spacing: Math.max(1, numberOr(analysis.drillSpacingCm, 25)),
    depth: Math.max(0, numberOr(analysis.drillDepthCm, 0)),
    angle: numberOr(analysis.drillAngleDeg, 45),
    mlPerHole: Math.max(0, numberOr(analysis.mlPerHole, 0)),
    bottles: Math.max(0, numberOr(analysis.calculatedBottles, 0)),
    total: Math.max(0, numberOr(analysis.totalPriceIsofin, 0)),
    firma: Math.max(0, numberOr(analysis.fachfirmaPrice, 0)),
    savings: Math.max(0, numberOr(analysis.savingsAmount, 0)),
    savingsPercent: Math.max(0, numberOr(analysis.savingsPercent, 0)),
    difficulty,
    workTimeMin,
    workTimeRangeText: analysis.workTimeRangeText || (workTimeMin > 0 ? `ca. ${Math.max(60, workTimeMin - 30)}–${workTimeMin + 45} min` : ''),
    drillMachineRecommendation: analysis.drillMachineRecommendation || 'Bohrhammer SDS+ ab 800 W',
    propertyValue,
    riskLossMin: propertyValue * 0.05,
    riskLossMax: propertyValue * 0.15,
  };
}

function loadPersistedAnalysis(calc) {
  try {
    if (calc && typeof calc.loadAnalysisFromStorage === 'function') {
      var fromCalc = calc.loadAnalysisFromStorage();
      if (fromCalc && typeof fromCalc === 'object') return fromCalc;
    }
  } catch (_) {}

  try {
    var raw = window.localStorage && window.localStorage.getItem('isofinAnalysis');
    if (raw) {
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch (_) {}

  return null;
}

/**
 * Berechnet abgeleitete Werte aus dem Projekt-State.
 * SEITENEFFEKT: Ruft calculateFinalAnalysis() auf, was in
 * localStorage schreibt und 'isodash-wiz:analysis-updated' dispatcht.
 * Ergebnisse werden global gecacht (JSON-Key-Vergleich).
 * @param {Object} project - Projekt-Objekt aus Store
 * @returns {Object} Abgeleitete Werte (bottles, total, savings, holes, etc.)
 */
function computeDerived(project) {
  var state = toCalcState(project);
  var economics = (project || {}).economics || {};
  var key = JSON.stringify({
    ...state,
    propertyValue: numberOr(economics.propertyValue, 0),
    livingArea: numberOr(economics.livingArea, 0),
  });
  if (cacheKey === key && cacheValue) return cacheValue;

  var calc = window.IsoDash && window.IsoDash.Calculations;
  if (calc && typeof calc.calculateFinalAnalysis === 'function') {
    try {
      var analysis = calc.calculateFinalAnalysis(state);
      cacheValue = toDerived(project, analysis || {});
      cacheKey = key;
      return cacheValue;
    } catch (err) {
      console.warn('[IsoDash Modules] calculateFinalAnalysis failed, fallback active', err);
    }
  }

  var persisted = loadPersistedAnalysis(calc);
  if (persisted) {
    cacheValue = toDerived(project, persisted);
    cacheKey = key;
    return cacheValue;
  }

  cacheValue = buildFallback(project);
  cacheKey = key;
  return cacheValue;
}

/* ═══ SHARED: Technische Freigabeprüfung (linke Spalte) ═══ */
var CHECK_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
var SHIELD_GAR_SVG = '<svg class="isodash-wiz-shield-gar" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1.5 14.59l-3.54-3.54 1.41-1.41 2.13 2.13 4.63-4.63 1.41 1.41-6.04 6.04z"/></svg>';

/**
 * Rendert die Technische Freigabeprüfung (Progress-Checklist) für die linke Spalte.
 * @param {number} activeStep - 0-basierter Index des aktiven Steps (0=Feuchteursache, 1=Wandmaße, 2=Mauerwerk, 3=Wandparameter, 4=Garantie)
 * @param {object} [opts] - Optionale Overrides: { moistureLabel: string }
 * @returns {string} HTML-String
 */
function renderFreigabeChecklist(activeStep, opts) {
  opts = opts || {};
  var items = [
    { label: 'Kapillarpr\u00fcfung', micro: 'Schadenstyp best\u00e4tigen' },
    { label: 'Feuchteursache bestimmt', micro: opts.moistureLabel || null },
    { label: 'Wandma\u00dfe erfassen', micro: 'L\u00e4nge & Ma\u00dfe eingeben' },
    { label: 'Mauerwerk pr\u00fcfen', micro: 'Steinart ausw\u00e4hlen' },
    { label: 'Produktmenge finalisieren', micro: 'Ecken & Wandst\u00e4rke' },
    { label: 'Dashboard & Ergebnis', micro: 'Bohrplan & Sanierungspaket', isGarantie: true }
  ];
  var mob = window.innerWidth < 768;

  /* Tracking: Welcher Schritt war beim letzten Render aktiv?
     Wenn jetzt gr\u00f6\u00dfer \u2192 der davor wurde "gerade fertig" \u2192 Pop-Animation. */
  var prevStep = (typeof window.__isodash_freigabe_lastStep === 'number')
    ? window.__isodash_freigabe_lastStep
    : -1;
  var justDoneIdx = (activeStep > prevStep && prevStep >= 0)
    ? Math.min(activeStep - 1, items.length - 1)
    : -1;
  window.__isodash_freigabe_lastStep = activeStep;

  /* Progress-Berechnung */
  var totalSteps = items.length;
  var doneCount = Math.max(0, Math.min(activeStep, totalSteps));
  var progressPct = Math.round((doneCount / totalSteps) * 100);

  function renderItem(i) {
    var it = items[i];
    var isDone = i < activeStep;
    var isActive = i === activeStep;
    var isFuture = i > activeStep;
    var isJustDone = (i === justDoneIdx);
    var cls = 'wiz-pi'
      + (isDone ? ' is-done' : '')
      + (isActive ? ' is-active' : '')
      + (isFuture ? ' is-future' : '')
      + (isJustDone ? ' is-just-done' : '')
      + (it.isGarantie ? ' is-garantie' : '');
    var r = '<div class="' + cls + '"><div class="wiz-pi-head">';
    if (isDone) {
      r += '<span class="wiz-pi-icon">' + CHECK_SVG + '</span>';
    } else if (isActive) {
      /* Pulsierender Dot statt Pfeil \u2014 signalisiert "wird live gepr\u00fcft" */
      r += '<span class="wiz-pi-icon"><span class="wiz-pi-pulse" aria-hidden="true"></span></span>';
    } else if (it.isGarantie) {
      r += '<span class="wiz-pi-icon wiz-pi-icon-shield">' + SHIELD_GAR_SVG + '</span>';
    } else {
      r += '<span class="wiz-pi-icon">' + (i + 1) + '</span>';
    }
    r += '<div class="wiz-pi-content"><span class="wiz-pi-label">' + it.label + '</span>';
    if (isActive) {
      r += '<span class="wiz-pi-micro wiz-pi-scan">' +
             '<span class="wiz-pi-scan-text">Wird live gepr\u00fcft</span>' +
             '<span class="wiz-pi-scan-dots"><i></i><i></i><i></i></span>' +
           '</span>';
    } else if (it.micro) {
      r += '<span class="wiz-pi-micro">' + (isDone ? '\u2713 ' : '') + it.micro + '</span>';
    }
    r += '</div></div>';
    if (isActive) {
      r += '<div class="wiz-pi-shimmer" aria-hidden="true"></div>';
    }
    r += '</div>';
    return r;
  }

  /* Kompakte <details>-Version: NUR EIN Item im Header sichtbar
     (kein Crossfade-Overlap mehr möglich).
     Indikator:
       - Steady: neutraler Loading-Spinner (grün/grau, dreht sich)
       - Bei Step-Wechsel: erst kurz Checkmark mit dem prev-Label,
         dann via JS-Swap auf neuen Spinner + neues Label.
     Der DOM-Swap (statt CSS-Crossfade) garantiert dass nie zwei
     Labels gleichzeitig sichtbar sind. */
  var activeIdx = Math.min(activeStep, items.length - 1);
  var activeLabel = items[activeIdx].label;
  var prevLabel = (justDoneIdx >= 0) ? items[justDoneIdx].label : null;
  var hasTransition = (prevLabel !== null);

  /* Initial state: prev (mit Check) bei Übergang, sonst direkt now (Spinner) */
  var initLabel = hasTransition ? prevLabel : activeLabel;
  var initMode = hasTransition ? 'done' : 'loading';

  /* Render-ID: damit nachgeschaltete setTimeout den richtigen DOM-Knoten findet
     (auch wenn der User in der Zwischenzeit weiter navigiert hat). */
  var renderId = 'fc-' + Date.now() + '-' + Math.floor(Math.random() * 1000);

  function _renderIndicator(mode) {
    if (mode === 'done') {
      return '<span class="wiz-fc-indicator wiz-fc-indicator-done">' + CHECK_SVG + '</span>';
    }
    return '<span class="wiz-fc-indicator wiz-fc-indicator-loading"><span class="wiz-fc-spinner" aria-hidden="true"></span></span>';
  }

  var h = '<details class="wiz-freigabe-compact" data-render-id="' + renderId + '"' +
          ' style="margin-top:' + (mob ? '16' : '24') + 'px !important">';
  h +=   '<summary class="wiz-fc-summary">';
  h +=     '<div class="wiz-fc-row">';
  h +=       '<div class="wiz-fc-states">';
  h +=         '<span class="wiz-fc-state wiz-fc-state-now" data-mode="' + initMode + '">';
  h +=           _renderIndicator(initMode);
  h +=           '<span class="wiz-fc-text">' + initLabel + '</span>';
  h +=         '</span>';
  h +=       '</div>';
  h +=       '<span class="wiz-fc-toggle" aria-hidden="true"></span>';
  h +=     '</div>';
  h +=     '<div class="wiz-freigabe-bar" aria-hidden="true"><div class="wiz-freigabe-bar-fill" style="width:' + progressPct + '%"></div></div>';
  h +=   '</summary>';
  h +=   '<div class="wiz-plist">';
  for (var i = 0; i < items.length; i++) { h += renderItem(i); }
  h +=   '</div>';
  h += '</details>';

  /* Bei Step-Wechsel: nach 800ms den Check + prev-Label gegen Spinner +
     active-Label austauschen. Slide-down-Animation für "wird nach unten
     weitergesetzt"-Feeling. JS-Swap verhindert zuverlässig Doppel-Render. */
  if (hasTransition) {
    setTimeout(function () {
      var stateEl = document.querySelector('[data-render-id="' + renderId + '"] .wiz-fc-state-now');
      if (!stateEl) return; /* User schon weiter navigiert → no-op */
      stateEl.classList.add('wiz-fc-leaving');
      setTimeout(function () {
        if (!document.body.contains(stateEl)) return;
        stateEl.setAttribute('data-mode', 'loading');
        stateEl.innerHTML = _renderIndicator('loading') +
          '<span class="wiz-fc-text">' + activeLabel + '</span>';
        stateEl.classList.remove('wiz-fc-leaving');
        stateEl.classList.add('wiz-fc-entering');
        setTimeout(function () {
          if (stateEl) stateEl.classList.remove('wiz-fc-entering');
        }, 260);
      }, 240);
    }, 800);
  }

  return h;
}

/**
 * P5: Kompakter Buy-Block am Ende eines Moduls.
 * Wird nur gerendert, wenn eine Berechnung vorliegt (Flaschen + Preis > 0).
 * Sonst nichts (auf der reinen Info-Seite ohne Berechnung sinnlos).
 *
 * @param {string} trackSource - Tracking-ID, z.B. 'isodash-checkout-bohrplan'
 * @param {object} [opts] - { compact: bool (default true) }
 * @returns {string} HTML-String, oder '' wenn keine Berechnung
 */
function renderBuyBlock(trackSource, opts) {
  /* DEAKTIVIERT (v10.6.120): Buy-Block-Pitch komplett raus auf jedem Modul.
     Buy-Widget rechts (Desktop) / Bottom-Sheet (Mobile) übernimmt Conversion.
     Funktion bleibt vorhanden, gibt aber leeren String zurück → keine HTML-Ausgabe. */
  return '';

  /* eslint-disable no-unreachable */
  opts = opts || {};
  var compact = opts.compact !== false;
  /* Nur rendern wenn Calculations vorhanden + Werte > 0 */
  var calc = window.IsoDash && window.IsoDash.Calculations;
  if (!calc || typeof calc.getFinalAnalysis !== 'function') return '';
  var a = calc.getFinalAnalysis();
  if (!a || !(a.calculatedBottles > 0) || !(a.totalPriceIsofin > 0)) return '';
  var bottles = Math.round(a.calculatedBottles);
  var total = a.totalPriceIsofin;
  var priceTxt = total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  var src = String(trackSource || 'isodash-checkout-module-buyblock').replace(/'/g, '');

  var h = '<div class="ds-buyblock' + (compact ? ' ds-buyblock--compact' : '') + '">';
  h +=   '<div class="ds-buyblock-info">';
  h +=     '<span class="ds-buyblock-icon" aria-hidden="true">🛒</span>';
  h +=     '<div class="ds-buyblock-text">';
  h +=       '<div class="ds-buyblock-headline">Bereit für Ihre Sanierung?</div>';
  h +=       '<div class="ds-buyblock-meta">' + bottles + ' Flaschen · ' + priceTxt + ' · kostenloser Versand</div>';
  h +=     '</div>';
  h +=   '</div>';
  h +=   '<button type="button" class="ds-buyblock-cta" onclick="ID.cart(\'' + src + '\')">';
  h +=     'Sanierungspaket bestellen <span class="ds-buyblock-arrow">→</span>';
  h +=   '</button>';
  h += '</div>';
  return h;
}

/* Utils self-registration */
window.IsoDashUtils = {
  createRuntimeScope: createRuntimeScope,
  getProject: getProject,
  escapeHTML: escapeHTML,
  masonryLabel: masonryLabel,
  formatEuro: formatEuro,
  computeDerived: computeDerived,
  renderFreigabeChecklist: renderFreigabeChecklist,
  renderBuyBlock: renderBuyBlock
};

})();
